import type {
  CartLine,
  DeliveryCoords,
  FulfillmentType,
  Order,
  OrderPaymentMethod,
  OrderStatus,
  PodStatus,
  Vendor,
} from '@shopgnd/shared';
import { mapAmplifyOrder, canViewOrderAsBuyer, canViewOrderAsVendorOwner } from '@shopgnd/api';

export { mapAmplifyOrder } from '@shopgnd/api';
import { decrementProductStock } from '@/api/catalog';
import { listVendors } from '@/api/catalog';
import { vendorDefaults, computeCheckoutDiscountCents, type CheckoutDiscountInput } from '@/lib/checkout';
import { addressFieldsForOrder } from '@/lib/vendorFulfillment';
import { commissionBpsForVendor, platformConvenienceFeeCents, vendorCommissionCents } from '@/lib/fees';
import { vendorAcceptsCardAtCheckout } from '@/lib/vendorPlanGates';
import { getDataClient } from '@/lib/amplify-client';
import { getIdToken } from '@/lib/auth';
import type { OrderPaymentStart } from '@/services/stripe';
import type { AuthUser } from '@/stores/authStore';

async function getStripeCheckoutUrl(): Promise<string | null> {
  const fromEnv = import.meta.env.VITE_STRIPE_CHECKOUT_URL as string | undefined;
  if (fromEnv) return fromEnv;
  try {
    const res = await fetch('/amplify_outputs.json');
    if (res.ok) {
      const outputs = (await res.json()) as { custom?: { stripeCheckoutUrl?: string } };
      return outputs.custom?.stripeCheckoutUrl ?? null;
    }
  } catch {
    /* missing outputs */
  }
  return null;
}

async function placeCheckoutOrdersLive(input: PlaceOrderInput): Promise<PlacedCheckout> {
  const url = await getStripeCheckoutUrl();
  const token = await getIdToken();
  if (!url || !token) {
    throw new Error('Checkout is not configured. Deploy the Amplify backend and sign in.');
  }

  const res = await fetch(`${url}?action=placeOrders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  let payload: PlacedCheckout & { error?: string };
  try {
    payload = (await res.json()) as PlacedCheckout & { error?: string };
  } catch {
    throw new Error(
      res.status === 404
        ? 'Checkout service not found — redeploy the Amplify backend.'
        : `Checkout failed (${res.status}). Try again or contact support.`,
    );
  }

  if (!res.ok) {
    throw new Error(payload.error ?? `Could not create order (${res.status})`);
  }

  return {
    orders: payload.orders ?? [],
    cardOrders: payload.cardOrders ?? [],
    codOrders: payload.codOrders ?? [],
    cardTotalCents: payload.cardTotalCents ?? 0,
    convenienceFeeCents: payload.convenienceFeeCents ?? 0,
  };
}

/** @deprecated Prefer resolveVendorForUserAsync — sync lookup is no longer supported. */
export function resolveVendorForUser(_user: AuthUser | null): Vendor | null {
  return null;
}

export async function resolveVendorForUserAsync(user: AuthUser | null): Promise<Vendor | null> {
  if (!user?.roles.includes('vendor')) return null;
  const vendors = await listVendors();
  return vendors.find((v) => v.ownerUserId === user.id) ?? null;
}

export async function listOrdersForBuyer(buyerUserId: string): Promise<Order[]> {
  try {
    const client = await getDataClient();
    const res = await client.models.Order.list({
      filter: { buyerUserId: { eq: buyerUserId } },
    });
    return ((res.data ?? []) as Record<string, unknown>[])
      .map(mapAmplifyOrder)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } catch (e) {
    console.error(e);
    return [];
  }
}

export async function listOrdersForVendor(vendorId: string): Promise<Order[]> {
  try {
    const client = await getDataClient();
    const res = await client.models.Order.list({
      filter: { vendorId: { eq: vendorId } },
    });
    return ((res.data ?? []) as Record<string, unknown>[])
      .map(mapAmplifyOrder)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } catch (e) {
    console.error(e);
    return [];
  }
}

/**
 * @deprecated Loads entire Order table via AppSync — use fetchAdminOrdersPage from @/api/adminOrders.
 */
export async function listAllOrders(): Promise<Order[]> {
  console.warn('[orders] listAllOrders is deprecated; use paginated admin-orders Lambda.');
  try {
    const client = await getDataClient();
    const res = await client.models.Order.list();
    return ((res.data ?? []) as Record<string, unknown>[])
      .map(mapAmplifyOrder)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } catch (e) {
    console.error(e);
    return [];
  }
}

export async function getOrder(orderId: string): Promise<Order | undefined> {
  try {
    const client = await getDataClient();
    const res = await client.models.Order.get({ id: orderId });
    if (res.data) return mapAmplifyOrder(res.data as Record<string, unknown>);
  } catch (e) {
    console.error(e);
  }
  return undefined;
}

export function canUserViewOrder(user: AuthUser | null, order: Order): boolean {
  if (!user) return false;
  return canViewOrderAsBuyer(user.roles, user.id, order);
}

export async function canUserViewOrderAsync(
  user: AuthUser | null,
  order: Order,
): Promise<boolean> {
  if (!user) return false;
  if (canViewOrderAsBuyer(user.roles, user.id, order)) return true;
  const vendor = await resolveVendorForUserAsync(user);
  return canViewOrderAsVendorOwner(vendor?.id, order);
}

export async function listPendingCardPaymentOrders(buyerUserId: string): Promise<Order[]> {
  const orders = await listOrdersForBuyer(buyerUserId);
  return orders.filter((o) => o.status === 'pending_payment' && o.paymentMethod === 'card');
}

export async function listPendingPaymentOrdersForCheckout(
  buyerUserId: string,
  checkoutId: string | undefined,
  orderId: string,
): Promise<Order[]> {
  const orders = await listOrdersForBuyer(buyerUserId);
  const pending = orders.filter((o) => o.status === 'pending_payment' && o.paymentMethod === 'card');
  if (checkoutId) {
    const group = pending.filter((o) => o.checkoutId === checkoutId);
    if (group.length > 0) return group;
  }
  const single = pending.find((o) => o.id === orderId);
  return single ? [single] : [];
}

export async function resumeCheckoutPayment(
  buyerUserId: string,
  order: Order,
): Promise<OrderPaymentStart> {
  const pending = await listPendingPaymentOrdersForCheckout(
    buyerUserId,
    order.checkoutId,
    order.id,
  );
  if (pending.length === 0) {
    throw new Error('This order is no longer awaiting payment.');
  }
  const orderIds = pending.map((o) => o.id);
  const amountCents = pending.reduce((s, o) => s + o.totalCents, 0);
  const { startOrderPayment } = await import('@/services/stripe');
  return startOrderPayment({
    orderIds,
    amountCents,
    checkoutId: order.checkoutId,
  });
}

export async function updateOrderStatus(
  orderId: string,
  status: OrderStatus,
  opts?: { trackingNumber?: string; podStatus?: PodStatus },
): Promise<Order | null> {
  const previous = await getOrder(orderId);
  try {
    const { hubUpdateOrderStatus, isHubApiConfigured } = await import('@/api/hub');
    if (await isHubApiConfigured()) {
      const result = await hubUpdateOrderStatus(orderId, status, {
        trackingNumber: opts?.trackingNumber,
        podStatus: opts?.podStatus,
        vendorId: previous?.vendorId,
        orgId: previous?.orgId,
      });
      const updated = mapAmplifyOrder(result.order as Record<string, unknown>);
      if (
        previous &&
        previous.status !== status &&
        (status === 'delivered' || status === 'collected')
      ) {
        const { requestOrderReviewPrompt } = await import('@/api/orderNotify');
        void requestOrderReviewPrompt(orderId);
      }
      return updated;
    }

    // Legacy: AppSync vendor update (removed for sellers after auth tighten — admins only).
    const client = await getDataClient();
    const input: Record<string, unknown> = { id: orderId, status };
    if (opts?.trackingNumber !== undefined) {
      input.trackingNumber = opts.trackingNumber;
    }
    if (opts?.podStatus !== undefined) {
      input.podStatus = opts.podStatus;
    }
    const res = await client.models.Order.update(input);
    if (res.data) {
      const updated = mapAmplifyOrder(res.data as Record<string, unknown>);
      if (
        previous &&
        previous.status !== status &&
        (status === 'delivered' || status === 'collected')
      ) {
        const { requestOrderReviewPrompt } = await import('@/api/orderNotify');
        void requestOrderReviewPrompt(orderId);
      }
      return updated;
    }
  } catch (e) {
    console.error(e);
  }
  return null;
}

export type PlaceOrderInput = {
  buyerUserId: string;
  lines: CartLine[];
  fulfillmentType: FulfillmentType;
  deliveryParish?: string;
  deliveryNote?: string;
  deliveryCoords?: DeliveryCoords;
  discounts?: CheckoutDiscountInput;
};

export type PlacedCheckout = {
  orders: Order[];
  cardOrders: Order[];
  codOrders: Order[];
  cardTotalCents: number;
  convenienceFeeCents: number;
};

/** Live checkout: creates orders via checkout Lambda (stock + DynamoDB). */
export async function placeCheckoutOrders(input: PlaceOrderInput): Promise<PlacedCheckout> {
  return placeCheckoutOrdersLive(input);
}

/** @deprecated Use placeCheckoutOrders */
export async function placePendingOrders(input: PlaceOrderInput): Promise<Order[]> {
  const result = await placeCheckoutOrders(input);
  return result.orders;
}
